import { Button, Card, Row, Select, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HTTPClient from "../../../utils/HTTPClient";
import { useTranslation } from "react-i18next";

const Github = ({ appName, app, getData, lastDeployDatetime }) => {
    const [isGithubConnected, setIsGithubConnected] = useState(false);
    const [repos, setRepos] = useState([]);
    const [connectedRepo, setConnectedRepo] = useState(null);
    const [selectedRepo, setSelectedRepo] = useState(null);
    const [repoBranches, setRepoBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [loadingConnectRepo, setLoadingConnectRepo] = useState(false);
    const [loadingDeploy, setLoadingDeploy] = useState(false);
    const [loadingAutoDeploy, setLoadingAutoDeploy] = useState(false);
    const { t } = useTranslation();

    const navigate = useNavigate();

    const onChange = (value) => {
        setSelectedRepo(value);
        setSelectedBranch(null);
    };

    const onChangeBranch = (value) => {
        setSelectedBranch(value);
    };

    const connectRepo = () => {
        if (!selectedRepo || !selectedBranch) {
            message.error(t("dettaglio_app.github.connect_repo.missing_param"));
            return;
        }
        setLoadingConnectRepo(true);
        const repoName = repos.find((repo) => repo.id === selectedRepo).full_name;
        HTTPClient.post("/appcustomizations/github/connect_repo", {
            appName,
            repoId: selectedRepo,
            appTechnology: app.technology,
            repoBranch: selectedBranch,
            repoName,
        })
            .then((res) => {
                getData(app.technology);
                message.success(t("dettaglio_app.github.connect_repo.success"));
                setLoadingConnectRepo(false);
            })
            .catch((e) => {
                message.error(t("dettaglio_app.github.connect_repo.error"));
                setLoadingConnectRepo(false);
            });
    };

    const doDeploy = () => {
        if (!connectedRepo) {
            message.error(t("dettaglio_app.github.do_deploy.missing_repo"));
            return;
        }
        setLoadingDeploy(true);
        HTTPClient.post("/appcustomizations/github/deploy", {
            appName,
            appTechnology: app.technology,
            entryfile: "main.py",
        })
            .then((res) => {
                message.success(t("dettaglio_app.github.do_deploy.success"));
                setLoadingDeploy(false);
            })
            .catch((e) => {
                message.error(t("dettaglio_app.github.do_deploy.error"));
                setLoadingDeploy(false);
            });
    };

    const doAutoDeploy = (status) => {
        setLoadingAutoDeploy(true);
        HTTPClient.post("/appcustomizations/github/autodeploy", {
            appName,
            appTechnology: app.technology,
            autodeploy: status,
        })
            .then((res) => {
                message.success(t("dettaglio_app.github.do_auto_deploy.success"));
                setLoadingAutoDeploy(false);
                getData(app.technology);
            })
            .catch((e) => {
                message.error(t("dettaglio_app.github.do_auto_deploy.error"));
                setLoadingAutoDeploy(false);
            });
    };

    useEffect(() => {
        // if there is a code in the url, then we are coming from github
        const currentUrl = window.location.href;
        const searchParams = new URLSearchParams(new URL(currentUrl).search);
        const code = searchParams.get("code");
        if (code) {
            HTTPClient.post("/appcustomizations/github/connect", { appName, code })
                .then((res) => {
                    getData(app.technology);
                    navigate(window.location.pathname);
                    message.success(t("dettaglio_app.github.success"));
                })
                .catch((e) => {
                    navigate(window.location.pathname);
                    message.error(t("dettaglio_app.github.error"));
                });
        }
        // check if github is connected
        HTTPClient.get("/appcustomizations/github/repos/" + appName)
            .then((res) => {
                if (res.data) {
                    setRepos(res.data.repos);
                    setConnectedRepo(res.data.connected_repo);
                    setSelectedRepo(res.data.connected_repo?.id);
                    setSelectedBranch(res.data.connected_repo?.branch);
                    setIsGithubConnected(res.data.is_connected);
                }
            })
            .catch((e) => {});
    }, []);

    useEffect(() => {
        if (repos && selectedRepo) {
            const full_name = repos.find((repo) => repo.id === selectedRepo).full_name;
            HTTPClient.post("/appcustomizations/github/branches", { full_name }).then((res) => {
                setRepoBranches(res.data);
            });
        }
    }, [selectedRepo]);

    return (
        <Card title="Github" style={{ width: "100%", marginTop: "15px" }}>
            <Row style={{ width: "100%", marginTop: "10px" }}>
                <Typography.Text>{t("dettaglio_app.github.info")}</Typography.Text>
            </Row>
            {isGithubConnected ? (
                <>
                    <Row style={{ width: "100%", marginTop: "10px", display: "flex", gap: "10px" }}>
                        <Select
                            onChange={onChange}
                            defaultValue={connectedRepo?.id}
                            style={{ width: "200px" }}>
                            {repos.map((repo) => {
                                return (
                                    <Select.Option value={repo.id}>{repo.full_name}</Select.Option>
                                );
                            })}
                        </Select>
                        <Select
                            onChange={onChangeBranch}
                            value={selectedBranch}
                            style={{ width: "200px" }}>
                            {repoBranches.map((branch) => {
                                return (
                                    <Select.Option value={branch.name}>{branch.name}</Select.Option>
                                );
                            })}
                        </Select>
                        <Button
                            type="primary"
                            style={{
                                "margin-right": "10px",
                                "margin-bottom": "5px",
                            }}
                            disabled={
                                !selectedBranch ||
                                !selectedRepo ||
                                (connectedRepo?.branch === selectedBranch &&
                                    connectedRepo?.id === selectedRepo)
                            }
                            loading={loadingConnectRepo}
                            onClick={connectRepo}>
                            {t("dettaglio_app.github.select_repo")}
                        </Button>
                    </Row>
                    {app.github_repo && (
                        <>
                            <Row
                                style={{
                                    width: "100%",
                                    marginTop: "10px",
                                    display: "flex",
                                    gap: "10px",
                                }}>
                                <Button
                                    type="primary"
                                    style={{ "margin-right": "10px", "margin-bottom": "5px" }}
                                    onClick={() => doDeploy()}
                                    loading={loadingDeploy}
                                    disabled={
                                        !connectedRepo ||
                                        !selectedRepo ||
                                        !selectedBranch ||
                                        connectedRepo?.branch !== selectedBranch ||
                                        connectedRepo?.id !== selectedRepo ||
                                        loadingAutoDeploy
                                    }>
                                    {t("dettaglio_app.github.do_deploy_button")}
                                </Button>
                            </Row>
                            {app.autodeploy ? (
                                <>
                                    <Row style={{ width: "100%", marginTop: "10px" }}>
                                        <Typography.Text>
                                            {t("dettaglio_app.github.stop_auto_deploy")}
                                        </Typography.Text>
                                    </Row>
                                    <Row style={{ width: "100%", marginTop: "10px" }}>
                                        <Button
                                            type="primary"
                                            danger
                                            style={{
                                                "margin-right": "10px",
                                                "margin-bottom": "5px",
                                            }}
                                            onClick={() => doAutoDeploy(false)}
                                            loading={loadingAutoDeploy}
                                            disabled={
                                                !connectedRepo ||
                                                !selectedRepo ||
                                                !selectedBranch ||
                                                connectedRepo?.branch !== selectedBranch ||
                                                connectedRepo?.id !== selectedRepo ||
                                                loadingDeploy
                                            }>
                                            {t("dettaglio_app.github.stop_auto_deploy")}
                                        </Button>
                                    </Row>
                                </>
                            ) : (
                                <>
                                    <Row style={{ width: "100%", marginTop: "10px" }}>
                                        <Typography.Text>
                                            {t("dettaglio_app.github.start_auto_deploy_info")}
                                        </Typography.Text>
                                    </Row>

                                    <Row style={{ width: "100%", marginTop: "10px" }}>
                                        <Button
                                            type="primary"
                                            style={{
                                                "margin-right": "10px",
                                                "margin-bottom": "5px",
                                            }}
                                            onClick={() => doAutoDeploy(true)}
                                            loading={loadingAutoDeploy}
                                            disabled={
                                                !connectedRepo ||
                                                !selectedRepo ||
                                                !selectedBranch ||
                                                connectedRepo?.branch !== selectedBranch ||
                                                connectedRepo?.id !== selectedRepo ||
                                                loadingDeploy
                                            }>
                                            {t("dettaglio_app.github.start_auto_deploy")}
                                        </Button>
                                    </Row>
                                </>
                            )}
                            {lastDeployDatetime && (
                                <Row style={{ width: "100%", marginTop: "10px" }}>
                                    {t("dettaglio_app.file_browser_deploy.last_deploy_datetime")}: {new Date(lastDeployDatetime).toLocaleString()} (UTC)
                                </Row>
                            )}
                        </>
                    )}
                </>
            ) : (
                <>
                    <Row style={{ width: "100%", marginTop: "10px" }}>
                        <Button
                            type="primary"
                            style={{ "margin-right": "10px", "margin-bottom": "5px" }}
                            onClick={() => {
                                window.location.href.includes(app?.technology)
                                    ? window.open(
                                          "https://github.com/login/oauth/authorize?client_id=4604494b4bd54de14bba&scope=repo&redirect_uri=" +
                                              window.location.href,
                                          "_self"
                                      )
                                    : window.open(
                                          "https://github.com/login/oauth/authorize?client_id=4604494b4bd54de14bba&scope=repo&redirect_uri=" +
                                              window.location.href +
                                              "/" +
                                              app?.technology,
                                          "_self"
                                      );
                            }}>
                            {t("dettaglio_app.github.call_to_action")}
                        </Button>
                    </Row>
                </>
            )}
        </Card>
    );
};

export { Github };
